import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Globals, Constants, Colors } from 'utils';
import { ValidationService, StorageService } from 'services';
import { BackIcon, UploadIcon, Download2Icon, TrashIcon } from 'assets/icons';
import { Button, PaperClip, Input, Modal, AreaSelector, Tooltip } from 'components';
import SignatureCanvas from 'react-signature-canvas';
import { SignImage } from 'assets/img';
import { VerificationFinishImage } from 'assets/img';
import { DocumentIcon } from 'assets/icons';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
}

declare var screen: any;

const STEPS = {
  FILES: 1,
  SIGN: 2,
};

class Validation extends React.Component<Props> {
  canvas: any;

  state = {
    step: STEPS.FILES,
    form: {
      inpre: '',
      identity_document: '',
      speciality: '',
      title: '',
      lawyer_document: '',
    },
    visible: true,
    visible_modal: false,
    areas: [] as any[],
    user_areas: [] as number[],
    // ─── Estado para modo replace ──────────────────────────────────────
    // Almacena las rutas de los archivos que el abogado subió previamente.
    // Se usa para mostrar los documentos existentes en la UI y para
    // determinar si se requiere volver a subir campos obligatorios.
    existingDocs: null as any,
    // Flags que indican si el usuario presionó X para eliminar un campo opcional.
    // Se envían al backend como remove_title/remove_speciality para que
    // sepa cuándo limpiar a null vs mantener el archivo anterior.
    removeTitle: false,
    removeSpeciality: false,
  };

  componentDidMount() {
    this.props.dispatch({
      type: 'SET_HEADER',
      payload: {
        title: 'Validar cuenta',
        onBack: null,
      },
    });

    this.props.dispatch({
      type: 'SET_SUBHEADER',
      payload: {
        title: '',
      },
    });

    this.load();
  }

  load = async () => {
    const res: any = await ValidationService.lawyer.check({
      user_id: this.props.user?.id,
    });
    if (
      res.user?.level_id != Constants.LEVELS.LAWYER ||
      res.user?.verified == Constants.VERIFIED.VERIFIED ||
      // En modo replace, los usuarios IN_PROCESS y REJECTED también pueden acceder
      // para actualizar sus documentos desde el banner-update-docs
      (res.user?.verified == Constants.VERIFIED.IN_PROCESS && !this.isReplace) ||
      (res.user?.verified == Constants.VERIFIED.REJECTED && !this.isReplace)
    ) {
      this.props.history.replace('/');
    }

    //  Precargar datos existentes en modo replace
    // Así el usuario ve exactamente qué tiene y decide qué quiere cambiar.
    if (this.isReplace) {
      this.setState({
        areas: res.areas || [],
        existingDocs: res.user_document || null,
        form: {
          ...this.state.form,
          lawyer_document: res.user?.lawyer_document || '',
        },
        user_areas: res.user_area_ids || [],
      });
    } else {
      this.setState({
        areas: res.areas || [],
      });
    }
  };

  back = (e: any) => {
    e.preventDefault();
    const { step } = this.state;

    if (step == STEPS.FILES) {
      this.props.history.goBack();
    } else {
      this.setStep(STEPS.FILES);
    }
  };

  setStep = (step: number) => {
    this.setState({
      step,
      visible: true,
    });
  };

  get isReplace() {
    // verifica si la URL tiene el parámetro replace=true
    return new URLSearchParams(this.props.location.search).get('replace') === 'true';
  }

  // Envía los documentos de verificación. Ya no se pide dibujar la firma
  // (ver next()), por lo que solo queda comentado el chequeo de firma
  // requerida y el envío del dataURL del canvas; el resto del envío del
  // formulario general (documentos, inpre, áreas, etc.) se mantiene activo.
  submit = (e: any = null) => {
    if (e) {
      e.preventDefault();
    }

    // COMENTADO: ya no se exige dibujar la firma antes de enviar.
    // if (this.state.visible) {
    //   Globals.showError('Por favor, ingrese su firma');
    //   return;
    // }

    Globals.confirm('¿Desea continuar con los documentos proporcionados?', async () => {
      const res: any = await ValidationService.lawyer.verify({
        user_id: this.props.user?.id,
        ...this.state.form,
        hasFile: true,
        // @ts-ignore
        inpre: this.state.form.inpre?.value,
        // @ts-ignore
        identity_document: this.state.form.identity_document?.value,
        // @ts-ignore
        speciality: this.state.form.speciality?.value,
        // @ts-ignore
        title: this.state.form.title?.value,
        // COMENTADO: ya no se envía la firma dibujada del canvas.
        // sign: this.canvas.toDataURL().replace('data:image/png;base64,', ''),
        sign: '',
        user_areas: JSON.stringify(this.state.user_areas),
      });

      this.setState({
        visible_modal: true,
      });

      if (res.user) {
        this.props.dispatch({
          type: 'SET_USER',
          payload: res.user,
        });
      }
    });
  };

  next = async () => {
    const { lawyer_document, identity_document, inpre } = this.state.form;

    //  Validaciones de campos obligatorios

    // Validar cédula: obligatoria a menos que ya exista en modo replace
    if (!identity_document && !(this.isReplace && this.state.existingDocs?.identity_document)) {
      Globals.showError('Por favor, suba la foto de la cédula o pasaporte');
      return;
    }

    if (!lawyer_document) {
      Globals.showError('Por favor, ingrese el número de INPRE');
      return;
    }

    // Validar INPRE (archivo): obligatorio a menos que ya exista en modo replace
    if (!inpre && !(this.isReplace && this.state.existingDocs?.inpre)) {
      Globals.showError('Por favor, suba el INPRE');
      return;
    }

    if (this.state.user_areas.length == 0) {
      Globals.showError('Por favor, ingrese al menos 1 tipo de derecho');
      return;
    }

    if (this.isReplace) {
      // si la URL tiene el parámetro replace=true actualiza los documentos
      this.replaceDocuments();
      return;
    }

    if (this.isReplace) {
      // si la URL tiene el parámetro replace=true actualiza los documentos
      this.replaceDocuments();
      return;
    }

    const res: any = await ValidationService.lawyer.checkInpre({
      user_id: this.props.user?.id,
      lawyer_document,
    });

    if (res.result) {
      // COMENTADO: ya no se pide dibujar la firma antes de enviar los
      // documentos en el registro nuevo de abogado. Se envía directo,
      // igual que en el modo de actualización de documentos.
      // this.setStep(STEPS.SIGN);
      this.submit();
    } else {
      Globals.showError('El número de inpre ya se encuentra registrado');
    }
  };

  replaceDocuments = () => {
    // REEMPLAZAR DOCUMENTOS en BD
    Globals.confirm('¿Desea reemplazar los documentos enviados anteriormente?', async () => {
      try {
        const res: any = await ValidationService.lawyer.updateDocs({
          user_id: this.props.user?.id,
          ...this.state.form,
          hasFile: true,
          // withoutError evita que el handler global muestre [object Object]
          withoutError: true,
          // @ts-ignore
          inpre: this.state.form.inpre?.value,
          // @ts-ignore
          identity_document: this.state.form.identity_document?.value,
          // @ts-ignore
          speciality: this.state.form.speciality?.value,
          // @ts-ignore
          title: this.state.form.title?.value,
          user_areas: JSON.stringify(this.state.user_areas),
          //  Flags de eliminación de campos opcionales
          remove_title: this.state.removeTitle ? 'true' : undefined,
          remove_speciality: this.state.removeSpeciality ? 'true' : undefined,
        });

        this.setState({ visible_modal: true });

        if (res.user) {
          this.props.dispatch({
            type: 'SET_USER',
            payload: res.user,
          });
        }
      } catch (err: any) {
        const message = err?.response?.data?.message;
        const errorText = Array.isArray(message) ? message[0] : message;
        Globals.showError(
          errorText ||
            'El número de INPRE ya está en uso por otro usuario. Cualquier duda contacte con soporte técnico.',
        );
      }
    });
  };

  clear = () => {
    this.canvas.clear();
    this.setState({
      visible: true,
    });
  };

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
      },
      callback,
    );
  };

  onClose = (e: any = null, close_page = false) => {
    if (e) {
      e.preventDefault();
    }
    this.setState(
      {
        visible_modal: false,
      },
      () => {
        if (close_page) {
          this.props.history.replace('/');
        }
      },
    );
  };

  download = () => {
    var a = document.createElement('a');
    a.href = this.canvas.toDataURL();
    a.download = 'forma.png';
    a.click();
  };

  delete = (e: any, index: number) => {
    e.preventDefault();
    let user_areas: any = [...this.state.user_areas];
    user_areas.splice(index, 1);
    this.setState({
      user_areas,
    });
  };

  render() {
    const { step, visible_modal } = this.state;

    if (!this.isReplace) {
      /* PAGINA DE VALIDACION DE NUEVO REGISTRO */
      return (
        <div id='lawyer-validation'>
          <Modal
            className='modal-success-validation'
            visible={visible_modal}
            title=''
            hideHeader={true}
            centered
            onClose={() => this.onClose()}
          >
            <div id='modal-success-validation'>
              <div className='text-center'>
                <img src={VerificationFinishImage} />
              </div>
              <h3>¡Su registro ha sido completado!</h3>
              <p>El administrador enviará a su correo la decisión respecto a su registro</p>
              <div className='text-center'>
                <a href='#' onClick={(e) => this.onClose(e, true)}>
                  Cerrar
                </a>
              </div>
            </div>
          </Modal>

          <Link to='/' className='back' onClick={(e: any) => this.back(e)}>
            <img src={BackIcon} />
          </Link>
          <form onSubmit={this.submit}>
            {/* COMENTADO: indicador de 2 pasos (Documentos / Firma). Con el
                paso de firma deshabilitado ya no hay un segundo paso real.
            <div className="container-steps">
              <div
                className={`item-step ${step >= STEPS.FILES ? 'active' : ''}`}
              >
                <p>Documentos</p>
              </div>
              <div className="separator"></div>
              <div
                className={`item-step ${step >= STEPS.SIGN ? 'active' : ''}`}
              >
                <p>Firma</p>
              </div>
            </div>
            */}
            <h4>{step == STEPS.FILES ? 'Cargar documentos' : 'Crear firma electrónica'}</h4>

            {step == STEPS.FILES && (
              <div className='container-white'>
                <AreaSelector
                  areas={this.state.areas}
                  userAreas={this.state.user_areas}
                  labelSelect='Haz clic en un tipo de derecho para agregarlo a tu perfil'
                  onChange={(updated) => this.setState({ user_areas: updated })}
                />

                <p className='subtitle' style={{ marginTop: '5px' }}>
                  Subir foto de cédula de identidad o pasaporte
                </p>
                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto o PDF del documento'
                  value={this.state.form.identity_document}
                  className={this.state.form.identity_document ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        identity_document: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>
                      {this.state.form.identity_document
                        ? 'Foto adjuntada'
                        : 'Subir foto o PDF del documento'}
                    </p>
                  </div>
                </PaperClip>
                <Input
                  type='number'
                  color='white'
                  value={this.state.form.lawyer_document}
                  name='lawyer_document'
                  label='Número de Inpre'
                  maxLength={7}
                  onChange={this.change}
                />
                <p className='subtitle'>Subir INPRE</p>
                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto o PDF del documento'
                  value={this.state.form.inpre}
                  className={this.state.form.inpre ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        inpre: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>{this.state.form.inpre ? 'Foto adjuntada' : 'Subir foto del documento'}</p>
                  </div>
                </PaperClip>
                <p className='subtitle'>Subir título (Foto o PDF) Opcional</p>
                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto del documento'
                  value={this.state.form.title}
                  className={this.state.form.title ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        title: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>{this.state.form.title ? 'Foto adjuntada' : 'Subir foto del documento'}</p>
                  </div>
                </PaperClip>
                <p className='subtitle'>Subir especialidad (Foto o PDF) Opcional</p>
                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto del documento'
                  value={this.state.form.speciality}
                  className={this.state.form.speciality ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        speciality: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>
                      {this.state.form.speciality ? 'Foto adjuntada' : 'Subir foto del documento'}
                    </p>
                  </div>
                </PaperClip>
                <div className='col-md-12 text-center'>
                  <Button type='button' className='btn-next' onClick={() => this.next()}>
                    Aceptar
                  </Button>
                </div>
              </div>
            )}

            {/* COMENTADO: paso de firma dibujada (STEPS.SIGN). Ya no se pide
                al enviar los documentos de verificación por primera vez.
                Ver next() / submitWithoutSign() y submit() (comentado más
                arriba, era el que usaba este canvas).
            {step == STEPS.SIGN && (
              <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="canvas-container">
                    <SignatureCanvas
                      ref={(ref: any) => (this.canvas = ref)}
                      onBegin={() => {
                        this.setState({
                          visible: false,
                        });
                      }}
                      canvasProps={{
                        width: screen.width < 600 ? screen.width - 80 : 600,
                        height: 200,
                        className: 'sigCanvas',
                      }}
                    />
                    {this.state.visible && (
                      <div className="container-image">
                        <img src={SignImage} />
                        <p>Dibuje su firma dentro del área punteada</p>
                      </div>
                    )}
                  </div>
                </div>
                {!this.state.visible && (
                  <div className="row row-buttons">
                    <div className="col-md-6">
                      <Button
                        type="button"
                        className="btn-clear"
                        onClick={() => this.clear()}
                      >
                        Repetir
                      </Button>
                    </div>
                    <div className="col-md-6 text-right">
                      <Button
                        type="button"
                        className="btn-download"
                        onClick={() => this.download()}
                      >
                        Descargar
                        <img src={Download2Icon} />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col-12 text-center">
                    <Button
                      type="button"
                      className="btn-next"
                      onClick={() => this.submit()}
                    >
                      Guardar
                    </Button>
                  </div>
                </div>
              </>
            )}
            */}
          </form>
        </div>
      );
    } else {
      /* PAGINA DE VALIDACION DE ACTUALIZACION DE DOCUMENTOS */
      return (
        <div id='lawyer-validation'>
          <Modal
            className='modal-success-validation'
            visible={visible_modal}
            title=''
            hideHeader={true}
            centered
            onClose={() => this.onClose()}
          >
            <div id='modal-success-validation'>
              <div className='text-center'>
                <img src={VerificationFinishImage} />
              </div>
              <h3>¡Su actualización de documentos ha sido enviada!</h3>
              <p>El administrador enviará a su correo la decisión respecto a su registo</p>
              <div className='text-center'>
                <a href='#' onClick={(e) => this.onClose(e, true)}>
                  Cerrar
                </a>
              </div>
            </div>
          </Modal>

          <Link to='/' className='back' onClick={(e: any) => this.back(e)}>
            <img src={BackIcon} />
          </Link>
          <form onSubmit={this.submit}>
            <h4>Actualizar documentos</h4>

            {step == STEPS.FILES && (
              <div className='container-white'>
                <AreaSelector
                  areas={this.state.areas}
                  userAreas={this.state.user_areas}
                  labelSelect='Haz clic en un tipo de derecho para agregarlo a tu perfil'
                  onChange={(updated) => this.setState({ user_areas: updated })}
                />

                <p className='subtitle' style={{ marginTop: '5px' }}>
                  Subir foto de cédula de identidad o pasaporte
                </p>

                {/* MOSTRAR DOCUMENTO EXISTENTE SI HAY UNO */}
                {/* CÉDULA DE IDENTIDAD */}
                {this.state.existingDocs?.identity_document && (
                  <div>
                    {/* Pre-signed URL: abre el documento de S3 mediante URL temporal */}
                    <a
                      href='#'
                      onClick={async (e: any) => {
                        e.preventDefault();
                        const opened = await StorageService.openFile(
                          this.state.existingDocs.identity_document,
                        );
                        if (!opened)
                          Globals.showError('No se pudo abrir el documento. Intente nuevamente.');
                      }}
                      className='download-file'
                    >
                      <img src={DocumentIcon} alt='Document Icon' />
                      <p> Ver documento actual </p>
                    </a>
                    <p className='text-muted mt-2'>
                      <small>
                        La carga de un nuevo documento sobreescribirá la versión anterior.
                      </small>
                    </p>
                  </div>
                )}

                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto o PDF del documento'
                  value={this.state.form.identity_document}
                  className={this.state.form.identity_document ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        identity_document: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>
                      {this.state.form.identity_document
                        ? 'Foto adjuntada'
                        : 'Subir foto o PDF del documento'}
                    </p>
                  </div>
                </PaperClip>
                <Input
                  type='number'
                  maxLength={7}
                  color='white'
                  value={this.state.form.lawyer_document}
                  name='lawyer_document'
                  label='Número de Inpre'
                  onChange={this.change}
                />
                <p className='subtitle'>Subir INPRE</p>

                {/* MOSTRAR DOCUMENTO EXISTENTE SI HAY UNO */}
                {this.state.existingDocs?.inpre && (
                  <div>
                    {/* Pre-signed URL: abre el documento de S3 mediante URL temporal */}
                    <a
                      href='#'
                      onClick={async (e: any) => {
                        e.preventDefault();
                        const opened = await StorageService.openFile(this.state.existingDocs.inpre);
                        if (!opened)
                          Globals.showError('No se pudo abrir el documento. Intente nuevamente.');
                      }}
                      className='download-file'
                    >
                      <img src={DocumentIcon} alt='Document Icon' />
                      <p> Ver documento actual </p>
                    </a>
                    <p className='text-muted mt-2'>
                      <small>
                        La carga de un nuevo documento sobreescribirá la versión anterior.
                      </small>
                    </p>
                  </div>
                )}

                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto o PDF del documento'
                  value={this.state.form.inpre}
                  className={this.state.form.inpre ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        inpre: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>{this.state.form.inpre ? 'Foto adjuntada' : 'Subir foto del documento'}</p>
                  </div>
                </PaperClip>
                <p className='subtitle'>Subir título (Foto o PDF) Opcional</p>

                {/* MOSTRAR DOCUMENTO EXISTENTE SI HAY UNO */}
                {this.state.existingDocs?.title && !this.state.removeTitle && (
                  <div>
                    {/* Pre-signed URL: abre el documento de S3 mediante URL temporal */}
                    <a
                      href='#'
                      onClick={async (e: any) => {
                        e.preventDefault();
                        const opened = await StorageService.openFile(this.state.existingDocs.title);
                        if (!opened)
                          Globals.showError('No se pudo abrir el documento. Intente nuevamente.');
                      }}
                      className='download-file'
                    >
                      <img src={DocumentIcon} alt='Document Icon' />
                      <p> Ver documento actual </p>
                      <Tooltip title='Eliminar archivo actual'>
                        <a
                          className='link-icon link-danger'
                          href='#'
                          onClick={(e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            Globals.confirm(
                              '¿Está seguro que desea eliminar este documento?',
                              () => {
                                this.setState({ removeTitle: true });
                              },
                            );
                          }}
                        >
                          <img src={TrashIcon} alt='Eliminar' />
                        </a>
                      </Tooltip>
                    </a>
                    <p className='text-muted mt-2'>
                      <small>
                        La carga de un nuevo documento sobreescribirá la versión anterior.
                      </small>
                    </p>
                  </div>
                )}

                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto del documento'
                  value={this.state.form.title}
                  className={this.state.form.title ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        title: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>{this.state.form.title ? 'Foto adjuntada' : 'Subir foto del documento'}</p>
                  </div>
                </PaperClip>
                <p className='subtitle'>Subir especialidad (Foto o PDF) Opcional</p>

                {/* MOSTRAR DOCUMENTO EXISTENTE SI HAY UNO */}
                {this.state.existingDocs?.speciality && !this.state.removeSpeciality && (
                  <div>
                    <a
                      href='#'
                      onClick={async (e: any) => {
                        e.preventDefault();
                        const opened = await StorageService.openFile(
                          this.state.existingDocs.speciality,
                        );
                        if (!opened)
                          Globals.showError('No se pudo abrir el documento. Intente nuevamente.');
                      }}
                      className='download-file'
                    >
                      <img src={DocumentIcon} alt='Document Icon' />
                      <p> Ver documento actual </p>
                      <Tooltip title='Eliminar archivo actual'>
                        <a
                          className='link-icon link-danger'
                          href='#'
                          onClick={(e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            Globals.confirm(
                              '¿Está seguro que desea eliminar este documento?',
                              () => {
                                this.setState({ removeSpeciality: true });
                              },
                            );
                          }}
                        >
                          <img src={TrashIcon} alt='Eliminar' />
                        </a>
                      </Tooltip>
                    </a>
                    <p className='text-muted mt-2'>
                      <small>
                        La carga de un nuevo documento sobreescribirá la versión anterior.
                      </small>
                    </p>
                  </div>
                )}

                <PaperClip
                  formats={[
                    'png',
                    'pdf',
                    'jpg',
                    'jpeg',
                    'heic',
                    'heif',
                    'webp',
                    'bmp',
                    'tiff',
                    'tif',
                    'avif',
                  ]}
                  successText='Foto adjuntada'
                  pendingText='Subir foto del documento'
                  value={this.state.form.speciality}
                  className={this.state.form.speciality ? 'active' : ''}
                  onChange={(e: any) => {
                    this.setState({
                      form: {
                        ...this.state.form,
                        speciality: e.target,
                      },
                    });
                  }}
                >
                  <div className='container-upload'>
                    <div className='container-icon'>
                      <img src={UploadIcon} />
                    </div>
                    <p>
                      {this.state.form.speciality ? 'Foto adjuntada' : 'Subir foto del documento'}
                    </p>
                  </div>
                </PaperClip>
                <div className='col-md-12 text-center'>
                  <Button type='button' className='btn-next' onClick={() => this.next()}>
                    Aceptar
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      );
    }
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(Validation);
